import type { PixelEvent } from '@/events';
import type { GoogleBrowserDestinationConfig } from '@/config/types/modules/integrations';

import { BaseDestinationAdapter } from '../../BaseDestinationAdapter';
import type { DestinationContext, DestinationResult } from '../../../types';
import {
  buildExternalPageViewKey,
  shouldSkipExternalPageView,
} from '../../../browser/ExternalPageViewDeduper';
import type { DestinationMetrics } from '../../../DestinationMetrics';
import { DestinationLogger } from '../../../DestinationLogger';
import { GoogleScriptLoader } from './GoogleScriptLoader';
import type { GoogleGtag } from './types';

export type GoogleBrowserDestinationDeps = {
  readonly config: GoogleBrowserDestinationConfig;
  readonly getSessionId: () => string;
  readonly logger: DestinationLogger;
  readonly metrics?: DestinationMetrics;
  readonly debug: boolean;
};

/**
 * Google gtag browser destination — PAGE_VIEW only.
 * Conversões server-side pertencem ao backend.
 */
export class GoogleBrowserDestination extends BaseDestinationAdapter {
  private readonly config: GoogleBrowserDestinationConfig;

  private readonly getSessionId: () => string;

  private readonly logger: DestinationLogger;

  private readonly metrics?: DestinationMetrics;

  private readonly debug: boolean;

  private readonly scriptLoader = new GoogleScriptLoader();

  private readonly tagIds: string[];

  private initialized = false;

  private gtag: GoogleGtag | null = null;

  constructor(deps: GoogleBrowserDestinationDeps) {
    super({
      name: 'google',
      version: '1.0.0',
      supportedTypes: new Set(['PAGE_VIEW']),
    });
    this.config = deps.config;
    this.getSessionId = deps.getSessionId;
    this.logger = deps.logger.forAdapter('GoogleBrowser');
    this.metrics = deps.metrics;
    this.debug = deps.debug;
    this.tagIds = [...new Set(deps.config.tagIds.filter(Boolean))];
  }

  supports(event: PixelEvent): boolean {
    return (
      this.config.enabled &&
      this.config.autoPageView !== false &&
      this.tagIds.length > 0 &&
      event.type === 'PAGE_VIEW'
    );
  }

  async ensureInit(timeoutMs: number): Promise<void> {
    if (this.initialized) return;
    if (!this.config.enabled || this.tagIds.length === 0) return;

    this.metrics?.recordInitAttempt('google');
    if (this.debug) this.logger.debug('destination_init_start');
    const primary = this.tagIds[0]!;

    if (this.config.loadScript !== false) {
      this.metrics?.recordScriptLoadAttempt('google');
      if (this.debug) this.logger.debug('script_load_start');
      try {
        this.gtag = await this.scriptLoader.load(primary, timeoutMs);
        this.metrics?.recordScriptLoadSuccess('google');
        if (this.debug) this.logger.debug('script_load_success');
      } catch (error) {
        this.metrics?.recordScriptLoadFailure('google');
        this.logger.error('script_load_failure', error);
        throw error;
      }
    } else if (
      typeof window !== 'undefined' &&
      (window as { gtag?: GoogleGtag }).gtag
    ) {
      this.gtag = (window as { gtag?: GoogleGtag }).gtag ?? null;
    }

    const gtag = this.gtag;
    if (!gtag) {
      this.metrics?.recordInitFailure('google');
      throw new Error('[GoogleBrowser] gtag unavailable');
    }

    let initFailed = false;
    for (const tagId of this.tagIds) {
      try {
        gtag('config', tagId, { send_page_view: false });
      } catch (error) {
        initFailed = true;
        this.metrics?.recordInitFailure('google');
        this.logger.error(`destination_init_failure id=${tagId}`, error);
      }
    }

    this.initialized = true;
    if (!initFailed) this.metrics?.recordInitSuccess('google');
    if (this.debug) this.logger.debug('destination_init_success');
  }

  async execute(context: DestinationContext): Promise<DestinationResult> {
    const startedAt = Date.now();
    const { event, signal, timeoutMs } = context;

    if (event.type !== 'PAGE_VIEW') {
      return this.createResult('ignored', Date.now() - startedAt, {
        ignored: true,
      });
    }

    if (signal.aborted) {
      return this.createResult('failed', Date.now() - startedAt, {
        error: 'Aborted',
      });
    }

    if (this.debug) this.logger.debug('pageview_received');

    try {
      await this.ensureInit(timeoutMs);
    } catch (error) {
      return this.createResult('failed', Date.now() - startedAt, {
        error: error instanceof Error ? error.message : String(error),
      });
    }

    const gtag =
      this.gtag ??
      (typeof window !== 'undefined'
        ? ((window as { gtag?: GoogleGtag }).gtag ?? null)
        : null);
    if (!gtag) {
      return this.createResult('failed', Date.now() - startedAt, {
        error: 'gtag unavailable',
      });
    }

    const path = event.path ?? '/';
    const url =
      event.url ??
      (typeof window !== 'undefined' ? window.location?.href : undefined) ??
      path;
    const title = typeof document !== 'undefined' ? document.title : undefined;
    const sessionId = this.getSessionId();
    let sent = 0;
    let deduped = 0;

    for (const tagId of this.tagIds) {
      const key = buildExternalPageViewKey(
        'google',
        tagId,
        'PAGE_VIEW',
        path,
        sessionId
      );

      if (shouldSkipExternalPageView(key)) {
        deduped += 1;
        if (this.debug)
          this.logger.debug(`pageview_skipped_dedupe id=${tagId}`);
        continue;
      }

      try {
        gtag('event', 'page_view', {
          send_to: tagId,
          page_location: url,
          page_path: path,
          page_title: title,
        });
        sent += 1;
        if (this.debug) this.logger.debug(`pageview_sent id=${tagId}`);
      } catch (error) {
        this.logger.error(`destination_execute_failure id=${tagId}`, error);
      }
    }

    if (sent === 0 && deduped > 0) {
      return this.createResult('skipped', Date.now() - startedAt, {
        skipped: true,
        metadata: { sent, deduped },
      });
    }

    return this.createResult('success', Date.now() - startedAt, {
      metadata: { sent, deduped, tagIds: this.tagIds },
    });
  }

  destroy(): void {
    this.scriptLoader.release();
    this.initialized = false;
    this.gtag = null;
  }
}
